import type { at } from '../generated/Beans';
import {
  addIfNotExists,
  BaseWebSocketInterface,
  removeItem
} from 'virtual-visit-common';
import { PortletStoreApi } from '../App';
import { useEffect } from 'react';
import { showErrorWithReloadButton } from '../utils/ComponentUtils';
import { intl } from 'ehs-react';
type SocketDataBean = at.sense.portlets.saf.virtualvisitpatient.model.SocketDataBean;
type WebSocketOperation = at.sense.portlets.saf.virtualvisitpatient.model.WebSocketOperation;
type WebSocketConfigBean = at.sense.portlets.saf.virtualvisitpatient.beans.config.WebSocketConfigBean;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Liferay: any;

export type SocketListener = (socketData: SocketDataBean) => void;

export class WebsocketInterface extends BaseWebSocketInterface {
  public readonly messageListeners: SocketListener[] = [];

  constructor(webSocketConfig: WebSocketConfigBean) {
    super();
    this.webSocketReconnectAttempts =
      webSocketConfig.websocketMaxReconnectCount;
    this.webSocketReconnectInterval = webSocketConfig.websocketReconnectDelay;
  }

  public connect(
    url: string,
    onError?: () => void,
    onReconnected?: () => void
  ): Promise<void> {
    console.log('connect web socket');
    return new Promise<void>(resolve => {
      this.connectInternal(url, resolve, onError, onReconnected);
    });
  }

  protected connectInternal(
    url: string,
    onOpen?: () => void,
    onError?: () => void,
    onReconnected?: () => void
  ): void {
    console.log('connectInternal');

    this.websocket = new WebSocket(url);
    (window as any).ws = this.websocket;

    this.websocket.onopen = event => {
      console.debug('onopen::', event);

      const socketData = {
        operation: 'FIRST_LOAD' as WebSocketOperation
      };

      console.debug('onopen::sending data:', socketData);
      this.send(socketData);
      if (onOpen) {
        onOpen();
      }
    };

    this.websocket.onmessage = event => {
      const msg = event.data;
      console.debug('onmessage::', msg);
      const response = JSON.parse(msg);
      // manually extend liferay session
      Liferay.Session.extend();

      if (response.callbackStatus) {
        this.messageListeners.forEach(lst => lst(response));
      }
    };

    if (onError) {
      this.websocket.onerror = event => onError();
    }

    this.registerCloseEventHandler(url, onOpen, onError, onReconnected);
  }

  public async send(socketData: Partial<SocketDataBean>): Promise<void> {
    console.log('web socket send', socketData);
    if (WebSocket.OPEN === this.websocket?.readyState) {
      console.debug('websocket is open, sending message:', socketData);
      this.websocket.send(JSON.stringify(socketData));
    } else {
      console.warn(
        'Websocket is not open, message will not be sent. ReadyState: ',
        this.websocket?.readyState
      );
    }
  }

  public async disconnect(): Promise<void> {
    if (WebSocket.OPEN === this.websocket?.readyState) {
      console.debug('websocket was open, it will be closed now.');
      this.websocket?.close();
    } else {
      console.warn(
        'Websocket is not open, it will not be closed. ReadyState:',
        this.websocket?.readyState
      );
    }
  }

  public addMessageListener(lst: SocketListener): void {
    addIfNotExists(this.messageListeners, lst);
  }

  public removeMessageLisener(lst: SocketListener): void {
    removeItem(this.messageListeners, lst);
  }
}

let websocketInterface: WebsocketInterface | undefined = undefined;

export function getWebsocketInterface(): WebsocketInterface {
  if (!websocketInterface) {
    websocketInterface = new WebsocketInterface(
      PortletStoreApi.getState().configuration.webSocketConfig
    );
  }
  return websocketInterface;
}

export function useWebsocketConnectionCheck(): void {
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        // Check if the WebSocket connection is still active
        console.log('ws ready state', (window as any).ws?.readyState);
        if (
          (window as any).ws &&
          (window as any).ws.readyState === WebSocket.OPEN
        ) {
          console.log('WebSocket connection is active');
        } else {
          console.error('WebSocket connection is not active');
          showErrorWithReloadButton(
            intl(
              'virtualvisitpatientportlet.websocket.error.message.notregistered.content'
            )
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
